import React, { useState } from 'react';

import { PlanChangeUpgradeModal } from 'src/routes/settings/plans/components/studio/PlanChangeUpgradeModal';
import {
  buildPlanChangeSelection,
  isResolvedStudioPlan,
} from 'src/routes/settings/plans/components/studio/studioPlanFeatures';
import { useStudioPlanChange } from 'src/routes/settings/plans/components/studio/useStudioPlanChange';
import {
  type StudioPlanId,
  useGetExtraAppsPreviewQuery,
  useGetStudioPlansQuery,
  usePurchaseExtraAppMutation,
} from 'src/services/api/studioCreditApi';

import { getUpgradeTarget } from './appLimit';
import type { AppLimitApp } from './AppLimitAppCard';
import {
  AppLimitModal,
  type AppLimitRoute,
  type AppLimitSurface,
} from './AppLimitModal';

interface AppLimitGateProps {
  onClose: () => void;
  /**
   * Resumes what the paywall interrupted — the build, or the pending app add.
   * Called only once the per-app fee has actually been charged, so the work it
   * starts is already paid for.
   */
  onProceed: () => void;
  /** The workspace's plan; decides which route out it is offered. */
  studioPlan: StudioPlanId;
  /** Which action the limit interrupted. */
  surface: AppLimitSurface;
  /** The app being held back, shown as the modal's card. */
  app?: AppLimitApp;
}

/**
 * Owns what happens after the app-limit modal.
 *
 * The fee route charges before it builds: confirming posts the purchase and the
 * modal stays open under a loading CTA until Stripe answers, so a declined card
 * is shown here — with the action still pending and retryable — instead of the
 * app being built and installed for free while the charge fails somewhere behind
 * it. Only a settled charge calls `onProceed`.
 *
 * The upgrade route hands off to the plans page's own plan-change flow
 * (`useStudioPlanChange` + `PlanChangeUpgradeModal`), so the upgrade actually
 * reaches Stripe rather than opening a browsing dialog. One upgrade
 * implementation, shared with the settings page.
 */
export const AppLimitGate: React.FC<AppLimitGateProps> = ({
  onClose,
  onProceed,
  studioPlan,
  surface,
  app,
}) => {
  // Local, rather than derived from the hook's `activeModal`: confirming or
  // cancelling the upgrade clears that state, which would otherwise drop the
  // user back onto the paywall they just acted on.
  const [isUpgrading, setIsUpgrading] = useState(false);
  // Set when the charge is refused, so the modal can say so and let the user try
  // again rather than closing on a failure the action depended on.
  const [hasChargeFailed, setHasChargeFailed] = useState(false);

  const [purchaseExtraApp, { isLoading: isCharging }] =
    usePurchaseExtraAppMutation();

  const {
    currentPlan,
    selection,
    isUpgrading: isSubmittingUpgrade,
    choosePlan,
    confirmUpgrade,
    closeModals,
  } = useStudioPlanChange();

  // One route out per plan, keyed off the plan the rest of the UI shows rather
  // than the usage read's `canAddPaidExtras`: that flag follows the Stripe
  // subscription, so a workspace on a paid plan without one would be pitched an
  // upgrade it does not need. Free is the only plan the backend hard-blocks from
  // extra apps, so it alone upgrades — to unlock paying per app at all.
  const route: AppLimitRoute = studioPlan === 'freemium' ? 'upgrade' : 'fee';
  const upgradeTarget =
    route === 'upgrade' ? getUpgradeTarget(studioPlan) : undefined;

  // Only fetched where the upgrade is the route on offer.
  const { data } = useGetStudioPlansQuery(undefined, { skip: !upgradeTarget });

  // Stripe's quote for one more app, read only where the fee is on offer — the
  // endpoint rejects Free outright. Normally already cached: `useAppLimit` warms
  // it on the surfaces that can open this modal, so the price paints on open
  // rather than after a proration round-trip. Still re-quoted on mount, since the
  // amount due now prorates against the time left in the period — but that runs
  // behind the cached price instead of a skeleton.
  const {
    data: feePreview,
    isError: isFeeUnquoted,
    refetch: refetchFeePreview,
  } = useGetExtraAppsPreviewQuery(undefined, {
    skip: route !== 'fee',
    refetchOnMountOrArgChange: true,
  });

  // Submitting needs both the catalog's tier for the target plan and a resolved
  // current plan to classify the change against — `choosePlan` discards a
  // selection it cannot classify — so the CTA stays disabled until both land
  // rather than swallowing a click.
  const upgradeSelection =
    upgradeTarget && isResolvedStudioPlan(currentPlan)
      ? buildPlanChangeSelection(upgradeTarget, currentPlan, data?.plans)
      : undefined;

  if (isUpgrading && selection) {
    return (
      <PlanChangeUpgradeModal
        isOpen
        selection={selection}
        creditLadder={data?.creditLadder ?? []}
        isLoading={isSubmittingUpgrade}
        onClose={() => {
          closeModals();
          onClose();
        }}
        onConfirm={(totalCredits) => {
          void confirmUpgrade(totalCredits);
        }}
      />
    );
  }

  // Charge, then hand back. The modal is deliberately left open across the await:
  // it is the only thing telling the user their click is still in flight, and on
  // a refusal it is where the reason belongs.
  const takeFeeThenProceed = async () => {
    setHasChargeFailed(false);
    try {
      await purchaseExtraApp().unwrap();
    } catch {
      setHasChargeFailed(true);
      return;
    }
    onProceed();
  };

  return (
    <AppLimitModal
      isOpen
      onClose={onClose}
      route={route}
      isConfirming={isCharging}
      hasChargeFailed={hasChargeFailed}
      upgradeTarget={upgradeTarget}
      feePreview={feePreview}
      isFeeUnquoted={isFeeUnquoted}
      onRetryFee={() => {
        void refetchFeePreview();
      }}
      isUpgradeReady={upgradeSelection !== undefined}
      surface={surface}
      app={app}
      onConfirm={() => {
        if (route === 'upgrade') {
          if (!upgradeSelection) return;
          setIsUpgrading(true);
          choosePlan(upgradeSelection);
          return;
        }
        void takeFeeThenProceed();
      }}
    />
  );
};
